import { useEffect, useRef, useState, useCallback } from "react";
import { useAba } from "@/lib/useAba";
import { Minus, Plus, Trash2, Receipt, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState } from "@/components/common";
import { PaymentDialog } from "@/components/PaymentDialog";
import { CashBar } from "@/pages/POS";
import { TicketPrint } from "@/components/ticketsale/TicketPrint";
import { RedeemTicket } from "@/components/ticketsale/RedeemTicket";
import { OptionsDialog } from "@/components/OptionsDialog";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, imgUrl, isAdminRole, isManager } from "@/lib/api";
import { directPrint } from "@/lib/qz";
import { ticketSlips } from "@/lib/receipts";
import { toast } from "sonner";
import { findByCode, useScanner } from "@/lib/barcode";

function ProductGrid({ products, onAdd }) {
  if (!products.length) return <EmptyState icon={Receipt} title="Nenhum produto liberado" text="Em Produtos, ative a opção 'Na Venda ticket' nos itens que podem ser vendidos aqui." />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="ticket-products">
      {products.map((p) => (
        <button key={p.id} onClick={() => onAdd(p)} className="card-surface card-hover p-4 text-left transition-transform active:scale-95" data-testid={`ticket-product-${p.id}`}>
          {p.image_url && <img src={imgUrl(p.image_url)} alt={p.name} className="mb-2 h-20 w-full rounded-md object-cover" />}
          <p className="font-semibold leading-tight">{p.name}</p>
          <p className="mt-1 font-mono text-lg font-bold text-primary">{brl(p.price)}</p>
        </button>
      ))}
    </div>
  );
}

function Cart({ cart, setQty, onPay, disabled }) {
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const units = cart.reduce((s, i) => s + i.qty, 0);
  return (
    <div className="card-surface p-4 flex flex-col gap-3 lg:sticky lg:top-24" data-testid="ticket-cart">
      <p className="eyebrow">Itens da venda</p>
      {cart.map((i) => (
        <div key={i.key} className="flex items-center gap-2" data-testid={`ticket-cart-item-${i.product_id}`}>
          <span className="flex-1 text-sm font-medium">{i.name}{i.options?.length > 0 && <span className="block text-xs text-muted-foreground">{i.options.map((o) => o.name).join(", ")}</span>}</span>
          <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => setQty(i.key, i.qty - 1)} data-testid={`ticket-dec-${i.product_id}`}>{i.qty === 1 ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}</Button>
          <span className="w-8 text-center font-mono font-bold" data-testid={`ticket-qty-${i.product_id}`}>{i.qty}</span>
          <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => setQty(i.key, i.qty + 1)} data-testid={`ticket-inc-${i.product_id}`}><Plus className="h-4 w-4" /></Button>
        </div>
      ))}
      {!cart.length && <p className="text-sm text-muted-foreground">Toque nos produtos ou passe o leitor de código de barras para adicionar.</p>}
      <div className="border-t pt-3 flex justify-between items-end"><span className="text-sm text-muted-foreground">{units} ticket(s)</span><span className="font-display text-3xl font-extrabold" data-testid="ticket-total">{brl(total)}</span></div>
      <Button size="lg" className="h-14 text-base" disabled={!cart.length || disabled} onClick={onPay} data-testid="ticket-pay-btn">Receber e imprimir tickets</Button>
    </div>
  );
}

export default function TicketSale() {
  const aba = useAba("vender");
  const { session } = useAuth();
  const [cash, setCash] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);
  const [picking, setPicking] = useState(null);
  const box = useRef(null);
  const loadCash = useCallback(() => api.get("/cash/current", { params: { mode: "ticket" } }).then((r) => setCash(r.data)), []);
  useEffect(() => { loadCash(); api.get("/products").then((r) => setProducts(r.data.filter((p) => p.ticket_sale && p.active !== false))); }, [loadCash]);
  const addLine = ({ product: p, options = [], qty = 1, notes = "", name, price }) => setCart((c) => {
    const key = `${p.id}|${JSON.stringify(options)}|${notes}`;
    return c.some((i) => i.key === key) ? c.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i))
      : [...c, { key, product_id: p.id, name: name || p.name, price: price ?? p.price, qty, options, notes }];
  });
  const add = (p) => (p.option_groups?.length ? setPicking(p) : addLine({ product: p }));
  useScanner((c) => { const p = findByCode(products, c); if (p) add(p); else toast.error(`Nenhum produto da Venda ticket com o código ${c}`); }, box, !!cash?.open);
  const setQty = (key, q) => setCart((c) => (q <= 0 ? c.filter((i) => i.key !== key) : c.map((i) => (i.key === key ? { ...i, qty: q } : i))));
  const confirm = async ({ payments, discount }) => {
    try {
      const { data } = await api.post("/ticket-sales", { items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty, options: i.options, notes: i.notes })), payments, discount });
      setPaying(false); setCart([]); setDone(data); loadCash();
      if (!(await directPrint(session, "ticket", (pr) => ticketSlips(data, session.tenant, pr)))) setTimeout(() => window.print(), 400);
    } catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const open = !!cash?.open;
  return (
    <>
      <PageHeader eyebrow="Venda direta" title="Venda ticket" subtitle={`Operador: ${session.user.name} · caixa individual`} />
      <Tabs key={aba} defaultValue={aba}>
        <TabsList><TabsTrigger value="vender" data-testid="ticket-tab-vender">Vender</TabsTrigger><TabsTrigger value="baixa" data-testid="ticket-tab-baixa">Baixar ticket</TabsTrigger></TabsList>
        <TabsContent value="vender" className="mt-4 space-y-4">
          {cash && <CashBar cash={cash} reload={loadCash} cashMode="ticket" />}
          {cash && !open ? (
            <EmptyState icon={Lock} title="Seu caixa está fechado" text={isManager(session.user) ? "Na Venda ticket cada operador tem o próprio caixa. Use o botão 'Abrir caixa' acima." : "Na Venda ticket cada operador tem o próprio caixa. Peça ao administrador ou gerente para abrir o seu."} />
          ) : (
            <div ref={box} className="grid lg:grid-cols-[1fr_360px] gap-4">
              <ProductGrid products={products} onAdd={add} />
              <Cart cart={cart} setQty={setQty} onPay={() => setPaying(true)} disabled={!open} />
            </div>
          )}
        </TabsContent>
        <TabsContent value="baixa" className="mt-4"><RedeemTicket /></TabsContent>
      </Tabs>
      <PaymentDialog open={paying} onOpenChange={setPaying} subtotal={cart.reduce((s, i) => s + i.price * i.qty, 0)} serviceFeePct={0} allowDiscount={false} title="Pagamento da venda ticket" onConfirm={confirm} methods={isAdminRole(session.user) ? null : session.user.ticket_payments} />
      <OptionsDialog product={picking} onClose={() => setPicking(null)} onAdd={addLine} />
      <TicketPrint sale={done} onClose={() => setDone(null)} />
    </>
  );
}
